import { useState, useCallback } from 'react'
import { supabase, TABLES } from '../lib/supabase'
import { addDays } from 'date-fns'

export const useSession = () => {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Fetch all sessions ordered by session_date
  const fetchAllSessions = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const { data, error } = await supabase
        .from(TABLES.SESSIONS)
        .select('*')
        .order('session_date', { ascending: true })

      if (error) throw error
      setSessions(data || [])
    } catch (err) {
      setError(err.message)
      console.error('Error fetching sessions:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch sessions within date range
  const fetchSessions = useCallback(async (startDate, endDate) => {
    setLoading(true)
    setError(null)
    
    try {
      const { data, error } = await supabase
        .from(TABLES.SESSIONS)
        .select('*')
        .gte('session_date', startDate.toISOString().split('T')[0])
        .lte('session_date', endDate.toISOString().split('T')[0])
        .order('session_date', { ascending: true })

      if (error) throw error
      setSessions(data || [])
    } catch (err) {
      setError(err.message)
      console.error('Error fetching sessions:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Get instructor view (filtered sessions)
  const getInstructorView = async (instructorId) => {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase
        .from(TABLES.INSTRUCTOR_SIGNUPS)
        .select(`
          *,
          SessionSchedule (*)
        `)
        .eq('instructor_id', instructorId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    } catch (err) {
      setError(err.message)
      console.error('Error fetching instructor view:', err)
      return []
    } finally {
      setLoading(false)
    }
  }

  // Instructor signup for session
  const instructorSignup = async (sessionId, instructorData) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.INSTRUCTOR_SIGNUPS)
        .insert({
          session_id: sessionId,
          instructor_id: instructorData.id,
          instructor_name: instructorData.name,
          instructor_email: instructorData.email,
          points_earned: instructorData.points,
          status: 'confirmed'
        })
        .select()

      if (error) throw error
      return { success: true, data }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // Cancel instructor signup
  const cancelSignup = async (signupId) => {
    try {
      const { error } = await supabase
        .from(TABLES.INSTRUCTOR_SIGNUPS)
        .delete()
        .eq('id', signupId)

      if (error) throw error
      return { success: true }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // Request substitute (48hr notice required)
  const requestSubstitute = async (signupId, reason) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.INSTRUCTOR_SIGNUPS)
        .update({
          status: 'substitute_requested',
          substitute_reason: reason,
          substitute_requested_at: new Date().toISOString()
        })
        .eq('id', signupId)
        .select()

      if (error) throw error
      return { success: true, data }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // Create recurring sessions (admin only)
  const createRecurringSessions = async (sessionData, weeks) => {
    setLoading(true)
    setError(null)
    try {
      const sessions = []
      const startDate = new Date(sessionData.start_date)
      
      for (let week = 0; week < weeks; week++) {
        const sessionDate = addDays(startDate, week * 7)
        sessions.push({
          ...sessionData,
          session_date: sessionDate.toISOString().split('T')[0],
          created_at: new Date().toISOString()
        })
      }

      const { data, error } = await supabase
        .from(TABLES.SESSIONS)
        .insert(sessions)
        .select()

      if (error) throw error
      return { success: true, data }
    } catch (err) {
      setError(err.message)
      console.error('Error creating recurring sessions:', err)
      return { success: false, error: err.message }
    } finally {
      setLoading(false)
    }
  }

  // Get user points
  const getUserPoints = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from(TABLES.POINTS)
        .select('total_points')
        .eq('user_id', userId)
        .single()

      if (error) throw error
      return data?.total_points || 0
    } catch (err) {
      console.error('Error fetching user points:', err)
      return 0
    }
  }, [])

  // Update user points
  const updateUserPoints = async (userId, points) => {
    try {
      const { error } = await supabase
        .from(TABLES.POINTS)
        .upsert({
          user_id: userId,
          total_points: points,
          updated_at: new Date().toISOString()
        })

      if (error) throw error
      return { success: true }
    } catch (err) {
      setError(err.message)
      return { success: false, error: err.message }
    }
  }

  // Create a new session in SessionSchedule table
  const createSession = async (formData) => {
    setLoading(true)
    setError(null)
    
    try {
      const durationMinutes = formData.duration_minutes || 135
      
      // Combine session_date and start_time into ISO datetime string
      const sessionDateTime = new Date(`${formData.session_date}T${formData.start_time}`)
      const sessionDateISO = sessionDateTime.toISOString()
      
      // Try with combined datetime first (in case table uses single datetime column)
      let sessionData = {
        title: formData.title,
        session_type: formData.session_type,
        session_date: sessionDateISO, // Combined datetime as ISO string
        duration_minutes: durationMinutes,
        location: formData.location,
        coach_name: formData.coach_name,
        description: formData.notes || null, // Use 'description' for notes (no 'notes' column exists)
        is_active: true, // Default to active
        created_at: new Date().toISOString()
      }

      // Try inserting into SessionSchedule table with combined datetime
      let { data, error } = await supabase
        .from('SessionSchedule')
        .insert(sessionData)
        .select()

      // If error suggests the table uses separate date/time fields, try that approach
      if (error && (error.message.includes('column') || error.message.includes('does not exist') || error.code === 'PGRST116')) {
        // Retry with separate date and time fields
        sessionData = {
          title: formData.title,
          session_type: formData.session_type,
          session_date: formData.session_date, // Just the date part (YYYY-MM-DD)
          start_time: formData.start_time,
          duration_minutes: durationMinutes,
          location: formData.location,
          coach_name: formData.coach_name,
          description: formData.notes || null, // Use 'description' for notes (no 'notes' column exists)
          is_active: true,
          created_at: new Date().toISOString()
        }
        
        const retryResult = await supabase
          .from('SessionSchedule')
          .insert(sessionData)
          .select()
        
        if (retryResult.error) throw retryResult.error
        return { success: true, data: retryResult.data }
      }

      if (error) throw error
      return { success: true, data }
    } catch (err) {
      const errorMessage = err.message || 'Failed to create session'
      setError(errorMessage)
      console.error('Error creating session:', err)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  return {
    sessions,
    loading,
    error,
    fetchAllSessions,
    fetchSessions,
    getInstructorView,
    instructorSignup,
    cancelSignup,
    requestSubstitute,
    createRecurringSessions,
    getUserPoints,
    updateUserPoints,
    createSession
  }
}
